import { Flex, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'
import { colorBorderDisabled } from 'src/app/themes/primaryThemeDefault'
import { Control, Controller, UseFormReturn } from 'react-hook-form'
import { NewAccountFormValues } from '../models'
import { useFormRules } from '../utils'
import { PROCESSING_TYPES } from '../constants'
import CustomSelect from 'src/app/shared/components/common/select'
import { IClient } from 'src/app/shared/models/clientSetup.model'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'

export interface ACHFormProps {
  control: Control<NewAccountFormValues>
  loading: boolean
  achExceptionProcessingType: string
  form: UseFormReturn<NewAccountFormValues>
  disabledForm?: boolean
  client?: IClient | null
}

const ACHForm: React.FC<ACHFormProps> = (props) => {
  const { client, control, loading, achExceptionProcessingType, form, disabledForm } = props
  const { t } = useTranslation()
  const rules = useFormRules()
  const { mode } = useWindowSize()
  const lookupValues = useSelector(getLookupValues)

  return (
    <Flex
      style={{
        flexDirection: 'column',
      }}
    >
      <Typography.Text
        strong
        style={{
          fontSize: 16,
          fontWeight: 700,
        }}
      >
        {t('AccountSetup:new_account_form.title.ACHExceptions')}
      </Typography.Text>
      <Flex
        style={{
          borderColor: colorBorderDisabled,
          borderWidth: 1,
          borderStyle: 'solid',
          borderRadius: 16,
          padding: 16,
          flexDirection: 'column',
          gap: 24,
          marginTop: 4,
        }}
      >
        <Controller
          control={control}
          name={'achExceptionProcessingType'}
          render={({ field, fieldState }) => (
            <CustomSelect
              label={t('AccountSetup:new_account_form.title.achExceptionProcessingType')}
              placeholder={t('AccountSetup:new_account_form.placeholder.achExceptionProcessingType')}
              value={!field.value ? null : field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error}
              options={lookupValues?.DefaultAchExceptionCreationType ?? []}
              loading={loading}
              disabled={disabledForm || client?.defaultAchExceptionCreationType === PROCESSING_TYPES.NOCREATE}
            />
          )}
          rules={rules.achExceptionProcessingType}
        />
        <Flex
          style={{
            gap: 24,
            display: achExceptionProcessingType === PROCESSING_TYPES.NOCREATE ? 'none' : 'flex',
            flexDirection: ['mobile', 'tablet'].includes(mode) ? 'column' : undefined,
          }}
        >
          <Controller
            control={control}
            name={'achDecision'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.achDecision')}
                placeholder={t('AccountSetup:new_account_form.placeholder.achDecision')}
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={async () => {
                  field.onBlur()
                  await form.trigger('achDecision')
                }}
                error={fieldState.error}
                options={lookupValues?.DefaultAchDecision ?? []}
                loading={loading}
                required={form.getValues('achExceptionProcessingType') === PROCESSING_TYPES.CREATE}
                disabled={disabledForm}
              />
            )}
            rules={{
              required: {
                value: form.getValues('achExceptionProcessingType') === PROCESSING_TYPES.CREATE,
                message: t('AccountSetup:new_account_form.validation.achDecisionRequired'),
              },
            }}
          />
          <Controller
            control={control}
            name={'achReason'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.achReason')}
                placeholder={t('AccountSetup:new_account_form.placeholder.achReason')}
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={async () => {
                  field.onBlur()
                  await form.trigger('achReason')
                }}
                error={fieldState.error}
                options={lookupValues?.DefaultAchReason ?? []}
                loading={loading}
                required={form.getValues('achExceptionProcessingType') === PROCESSING_TYPES.CREATE}
                disabled={disabledForm}
              />
            )}
            rules={{
              required: {
                value: form.getValues('achExceptionProcessingType') === PROCESSING_TYPES.CREATE,
                message: t('AccountSetup:new_account_form.validation.achReasonRequired'),
              },
            }}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}

export default ACHForm
